import { useMemo } from 'react';
import {
  EnumCutsheetTrimTrimSelection,
  ProcessorCapability,
  ProcessorCapabilityTrim,
} from 'lib/graphql';
import { CutsheetWizardFormValues, TrimOption, TrimSelection } from '../types';

export function useTrimOptions(
  activeCapability: ProcessorCapability | undefined,
  values: CutsheetWizardFormValues,
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean,
  ) => void,
) {
  /**
   * Extract trim options with specification-first priority.
   *
   * Same priority logic as extras:
   * 1. Check activeSpecification.trim FIRST (specification-specific overrides)
   * 2. Fall back to cap.trim (base capability defaults)
   *
   * Trim represents what happens to the leftover meat after cuts are selected
   * (e.g., "Ground Beef", "80/20 Ground", "Burger Patties"). Processors can customize
   * trim options and pricing per specification to handle different quality levels or
   * customer preferences.
   */
  const trimsRaw = useMemo<ProcessorCapabilityTrim[]>(() => {
    const cap = activeCapability;
    const candidates = [
      // Priority 1: Specification-specific trim (overrides)
      (cap?.activeSpecification as { trim?: ProcessorCapabilityTrim[] })?.trim,
      // Priority 2: Base capability trim (defaults)
      cap?.trim,
    ];
    for (const cand of candidates) {
      if (Array.isArray(cand) && cand.length)
        return cand.filter((t): t is ProcessorCapabilityTrim => !!t);
    }
    return [];
  }, [activeCapability]);

  const trimOptions = useMemo(
    () =>
      trimsRaw
        .filter((t) => t && (t.isActive ?? true))
        .map(
          (t): TrimOption => ({
            value: String(t?.name ?? t?._id ?? ''),
            label: String(t?.name ?? ''),
            description: t?.description ?? '',
            isActive: t?.isActive ?? false,
            minLbs: t?.minLbs ?? 0,
            pricePerPound: t?.pricePerPound ?? 0,
          }),
        )
        .filter((o) => !!o.value && !!o.label),
    [trimsRaw],
  );

  const onToggleTrim = (
    value: string,
    checked: boolean,
    option: TrimOption,
  ) => {
    const currentTrim = values.trim || [];
    const selectionMode = values.trimSelection || 'allowSelectionByWeight';
    const trimRaw = trimsRaw.find(
      (t) => String(t?.name ?? t?._id ?? '') === value,
    );

    if (!trimRaw) return;

    if (checked) {
      // Add new trim with format that matches CutsheetTrimInput
      const nextRank =
        Array.isArray(currentTrim) && currentTrim.length > 0
          ? Math.max(...currentTrim.map((t) => t.rank || 0)) + 1
          : 1;

      const quantity =
        selectionMode === 'allowSelectionByPercentage'
          ? currentTrim.length === 0
            ? 100
            : currentTrim.length === 1
              ? 50
              : 0
          : option.minLbs || 0;

      const newTrimSelection: TrimSelection = {
        _id: trimRaw._id,
        name: String(trimRaw.name ?? ''),
        isActive: true,
        pricePerPound: trimRaw.pricePerPound ?? undefined,
        minLbs: trimRaw.minLbs ?? undefined,
        quantity,
        rank: nextRank,
        trimSelection: selectionMode as EnumCutsheetTrimTrimSelection,
        isAllTrim: false,
      };

      let updatedTrim: TrimSelection[];

      if (
        selectionMode === 'allowSelectionByPercentage' &&
        currentTrim.length === 1
      ) {
        // If this is the second selection in percentage mode, set both to 50/50
        updatedTrim = [
          { ...currentTrim[0], quantity: 50 },
          { ...newTrimSelection, quantity: 50 },
        ];
      } else {
        updatedTrim = [...currentTrim, newTrimSelection];
      }

      setFieldValue('trim', updatedTrim, true);
    } else {
      // Remove trim
      const filtered = currentTrim.filter((t) => t.name !== trimRaw.name);

      // Reorder ranks and handle percentage mode
      const normalized = filtered.map((v, index) => ({
        ...v,
        rank: index + 1,
        ...(selectionMode === 'allowSelectionByPercentage' &&
        filtered.length === 1
          ? { quantity: 100 }
          : {}),
      }));

      setFieldValue('trim', normalized, true);
    }
  };

  const onTrimRankUp = (trimName: string) => {
    const currentTrim = values.trim || [];
    if (!Array.isArray(currentTrim)) return;

    const index = currentTrim.findIndex((t) => t.name === trimName);
    if (index <= 0) return; // Already at top or not found

    const updated = [...currentTrim];
    const item = updated[index];
    const prevItem = updated[index - 1];

    // Swap ranks
    const tempRank = item.rank;
    item.rank = prevItem.rank;
    prevItem.rank = tempRank;

    // Re-sort by rank
    updated.sort((a, b) => (a.rank || 0) - (b.rank || 0));

    // Normalize ranks to sequential order (1, 2, 3...)
    const normalized = updated.map((t, idx) => ({
      ...t,
      rank: idx + 1,
    }));

    setFieldValue('trim', normalized, true);
  };

  const onTrimRankDown = (trimName: string) => {
    const currentTrim = values.trim || [];
    if (!Array.isArray(currentTrim)) return;

    const index = currentTrim.findIndex((t) => t.name === trimName);
    if (index === -1 || index >= currentTrim.length - 1) return; // Not found or already at bottom

    const updated = [...currentTrim];
    const item = updated[index];
    const nextItem = updated[index + 1];

    // Swap ranks
    const tempRank = item.rank;
    item.rank = nextItem.rank;
    nextItem.rank = tempRank;

    // Re-sort by rank
    updated.sort((a, b) => (a.rank || 0) - (b.rank || 0));

    // Normalize ranks to sequential order (1, 2, 3...)
    const normalized = updated.map((t, idx) => ({
      ...t,
      rank: idx + 1,
    }));

    setFieldValue('trim', normalized, true);
  };

  const onTrimQuantityChange = (trimName: string, quantity: number) => {
    const currentTrim = values.trim || [];
    if (!Array.isArray(currentTrim)) return;

    const updated = currentTrim.map((t) => {
      if (t.name === trimName) {
        return {
          ...t,
          quantity,
        };
      }
      return t;
    });
    setFieldValue('trim', updated, true);
  };

  // Check if a trim option is selected
  const isTrimSelected = (trimName: string) =>
    (values.trim || []).some((t) => t.name === trimName && t.isActive);

  // Get the trim selection for a specific trim option
  const getTrimSelection = (trimName: string): TrimSelection | null =>
    (values.trim || []).find((t) => t.name === trimName && t.isActive) || null;

  // Percentage validation for trim selections
  const trimValidation = useMemo(() => {
    if (!values.trim || !Array.isArray(values.trim)) {
      return { isValid: true, total: 0, message: '' };
    }

    const selectedTrim = values.trim.filter((t) => t.isActive);
    const isPercentageMode =
      values.trimSelection === 'allowSelectionByPercentage';

    if (!isPercentageMode || selectedTrim.length === 0) {
      return { isValid: true, total: 0, message: '' };
    }

    const total = selectedTrim.reduce((sum, t) => {
      const qty = t.quantity ?? 0;
      return sum + (Number.isNaN(qty) ? 0 : qty);
    }, 0);

    // Check if all selected trim have valid quantities
    const hasInvalidEntries = selectedTrim.some((t) => {
      const qty = t.quantity ?? 0;
      return qty <= 0 || Number.isNaN(qty);
    });

    if (hasInvalidEntries) {
      return {
        isValid: false,
        total,
        message:
          'All selected trim options must have a value. Trim percentages must add up to 100%.',
      };
    }

    const isValid = Math.abs(total - 100) < 0.01; // Allow small rounding errors
    const message = isValid ? '' : 'Trim percentages must add up to 100%';

    return { isValid, total, message };
  }, [values.trim, values.trimSelection]);

  return {
    trimOptions,
    trimsRaw,
    onToggleTrim,
    onTrimRankUp,
    onTrimRankDown,
    onTrimQuantityChange,
    isTrimSelected,
    getTrimSelection,
    trimValidation,
  };
}
